import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Fuel, Gauge, Cpu, SlidersHorizontal, ArrowUpRight } from "lucide-react";
import { services } from "@/data/services";
import { priceLabelForSlug } from "@/data/pricing";

const MAIN_SLUGS = ["reprogrammation-e85", "reprogrammation-diesel", "diagnostic-electronique", "cartographie-sur-mesure"];
const STAGE_ORDER = ["stage-1", "stage-1-plus", "stage-2", "stage-3"];
const extra = [
  ...STAGE_ORDER.map((s) => services.find((x) => x.slug === s)).filter(Boolean),
  ...services.filter((s) => !MAIN_SLUGS.includes(s.slug) && !STAGE_ORDER.includes(s.slug)),
];

const MotionLink = motion.create(Link);

const servicesCards = [
  {
    num: "01",
    slug: "reprogrammation-e85",
    icon: Fuel,
    title: "Reprogrammation E85",
    accent: "#6EB000",
    desc: "Roulez au bioéthanol : jusqu'à ≈ 1,20 €/L d'économie à la pompe, tout en gardant votre voiture. Étude de compatibilité sérieuse avant toute intervention.",
    tags: ["Économie carburant", "Compatible SP95"],
    big: true,
  },
  {
    num: "02",
    slug: "reprogrammation-diesel",
    icon: Gauge,
    title: "Reprogrammation diesel",
    accent: "#F5A623",
    desc: "Couple moteur optimisé pour une conduite plus souple. Selon votre utilisation, la consommation peut être réduite — sans promesse excessive.",
    tags: ["Couple optimisé", "Conso réduite"],
    big: true,
  },
  {
    num: "03",
    slug: "diagnostic-electronique",
    icon: Cpu,
    title: "Diagnostic électronique",
    accent: "#6EB000",
    desc: "Lecture et analyse complète de l'électronique embarquée avant chaque cartographie.",
    tags: ["Analyse complète"],
    big: false,
  },
  {
    num: "04",
    slug: "cartographie-sur-mesure",
    icon: SlidersHorizontal,
    title: "Cartographie sur mesure",
    accent: "#F5A623",
    desc: "Chaque moteur est unique. Votre cartographie est réalisée sur mesure, selon votre véhicule et votre usage.",
    tags: ["100% sur mesure"],
    big: false,
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-24 sm:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-14"
        >
          <p className="font-script text-2xl text-[#6EB000] mb-2">Ce que je fais pour vous</p>
          <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl text-white">
            Mes <span className="text-[#6EB000]">services</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4">
          {servicesCards.map((s, i) => (
            <MotionLink
              key={s.num}
              to={`/services/${s.slug}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              data-testid={`service-card-${s.num}`}
              className={`group relative bg-[#343A3D] border border-[#454C50] hover:border-[#6EB000] transition-colors duration-300 p-7 sm:p-9 overflow-hidden ${
                s.big ? "md:min-h-[300px]" : ""
              }`}
            >
              <span className="absolute -top-4 -right-2 font-display text-[7rem] leading-none text-white/[0.04] select-none">
                {s.num}
              </span>
              <div className="relative">
                <div className="flex items-start justify-between">
                  <s.icon className="w-9 h-9" style={{ color: s.accent }} strokeWidth={1.6} />
                  <ArrowUpRight className="w-5 h-5 text-white/20 group-hover:text-[#6EB000] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl text-white mt-6">
                  {s.title}
                </h3>
                <p className="font-display text-lg mt-2" style={{ color: s.accent }} data-testid={`service-card-price-${s.num}`}>
                  {priceLabelForSlug(s.slug)}
                </p>
                <p className="text-white/60 text-sm sm:text-base mt-3 leading-relaxed max-w-md">{s.desc}</p>
                <div className="flex flex-wrap gap-2 mt-6">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[11px] uppercase tracking-widest px-3 py-1 border border-[#454C50] text-white/50 group-hover:border-[#6EB000]/40 transition-colors duration-300"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </MotionLink>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-12"
        >
          <p className="uppercase tracking-widest text-white/40 text-xs mb-5">Prestations additionnelles</p>
          <div className="flex flex-wrap gap-3" data-testid="services-extra-links">
            {extra.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-extra-${s.slug}`}
                className="px-5 py-3 border border-[#454C50] hover:border-[#F5A623] text-white/70 hover:text-[#F5A623] text-sm uppercase tracking-widest transition-colors duration-300"
              >
                {s.title}
                <span className="normal-case tracking-normal text-[#F5A623]/80"> · {priceLabelForSlug(s.slug)}</span>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
